<template>
    <!-- the merchant portal Transactions "custom date range": two months, the first click sets the
         start, the second the end, the days between on a grey band (tasks#211) -->
    <div :class="['dd-range', { 'dd-range--blocked': blocked }]">
        <div class="dd-range__months">
            <div
                v-for="(month, index) in months"
                :key="month.key"
                class="dd-range__month"
            >
                <div class="dd-range__head">
                    <v-btn
                        v-if="index === 0"
                        icon
                        class="dd-range__nav"
                        :aria-label="
                            $vuetify.lang.t(
                                '$vuetify.datePicker.prevMonthAriaLabel'
                            )
                        "
                        @click="shift(-1)"
                    >
                        <v-icon>mm-chevron-left</v-icon>
                    </v-btn>
                    <span class="dd-range__title">{{ month.title }}</span>
                    <v-btn
                        v-if="index === months.length - 1"
                        icon
                        class="dd-range__nav dd-range__nav--next"
                        :aria-label="
                            $vuetify.lang.t(
                                '$vuetify.datePicker.nextMonthAriaLabel'
                            )
                        "
                        @click="shift(1)"
                    >
                        <v-icon>mm-chevron-right</v-icon>
                    </v-btn>
                </div>
                <table class="dd-range__grid" role="grid">
                    <thead>
                        <tr>
                            <th v-for="day in weekDays" :key="day">
                                {{ day }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(week, w) in month.weeks" :key="w">
                            <td
                                v-for="(day, d) in week"
                                :key="d"
                                role="gridcell"
                                :class="day && dayClass(day)"
                            >
                                <button
                                    v-if="day"
                                    type="button"
                                    class="dd-range__day"
                                    :disabled="blocked"
                                    :aria-pressed="String(isEdge(day))"
                                    @click="pick(day)"
                                >
                                    <span>{{ day.date() }}</span>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <button
            v-if="start"
            type="button"
            class="dd-range__clear"
            :disabled="blocked"
            @click="clear"
        >
            {{ clearText }}
        </button>
    </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { EventBus } from '@/plugins/events'

// field = { start: <date filter>, end: <date filter> }, two filters of TableFilters merged into one
// pill; it writes the start of the first and the end of the last day, like the Today chip does
export default {
    props: {
        value: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        // one month, as on a phone (the More filters side panel)
        singleMonth: {
            type: Boolean,
        },
    },
    data() {
        return {
            // first day of the left month
            cursor: this.$date().startOf('month'),
            blocked: false,
        }
    },
    computed: {
        start() {
            return this.read(this.field.start)
        },
        end() {
            return this.read(this.field.end)
        },
        monthCount() {
            return this.singleMonth || this.$vuetify.breakpoint.xsOnly ? 1 : 2
        },
        // Monday first, like the UK calendar
        weekDays() {
            return [1, 2, 3, 4, 5, 6, 0].map((d) =>
                this.$date().day(d).format('dd')
            )
        },
        months() {
            return [...Array(this.monthCount).keys()].map((i) => {
                const first = this.cursor.add(i, 'month')
                const offset = (first.day() + 6) % 7
                const days = [...Array(offset).fill(null)]
                for (let d = 0; d < first.daysInMonth(); d++)
                    days.push(first.add(d, 'day'))
                const weeks = []
                while (days.length) weeks.push(days.splice(0, 7))
                const last = weeks[weeks.length - 1]
                while (last.length < 7) last.push(null)
                return {
                    key: first.format('YYYY-MM'),
                    title: first.format('MMMM YYYY'),
                    weeks,
                }
            })
        },
        listener() {
            return this.field.start.listener || this.field.end.listener
        },
        clearText() {
            return this.$te('actions.clear')
                ? this.$t('actions.clear')
                : 'Clear'
        },
    },
    created() {
        if (this.listener) EventBus.$on(this.listener, this.block)
        if (this.start) this.cursor = this.start.startOf('month')
    },
    beforeDestroy() {
        if (this.listener) EventBus.$off(this.listener, this.block)
    },
    methods: {
        block(state) {
            this.blocked = state
        },
        read(filter) {
            const raw = filter.type
                ? this.value[filter.key]?.[filter.type]
                : this.value[filter.key]
            return raw ? this.$date(raw).startOf('day') : null
        },
        shift(months) {
            this.cursor = this.cursor.add(months, 'month')
        },
        isEdge(day) {
            return Boolean(
                (this.start && day.isSame(this.start, 'day')) ||
                    (this.end && day.isSame(this.end, 'day'))
            )
        },
        dayClass(day) {
            const start = this.start
            const end = this.end
            const ranged = start && end && !start.isSame(end, 'day')
            return {
                'dd-range__cell--today': day.isSame(this.$date(), 'day'),
                'dd-range__cell--edge': this.isEdge(day),
                'dd-range__cell--start': ranged && day.isSame(start, 'day'),
                'dd-range__cell--end': ranged && day.isSame(end, 'day'),
                'dd-range__cell--in':
                    ranged &&
                    day.isAfter(start, 'day') &&
                    day.isBefore(end, 'day'),
            }
        },
        // first click: start (a one-day range); second click on a later day: the end; any other
        // click starts a new range
        pick(day) {
            const start = this.start
            const end = this.end
            if (
                start &&
                end &&
                start.isSame(end, 'day') &&
                day.isAfter(start, 'day')
            )
                this.write(start, day)
            else this.write(day, day)
        },
        clear() {
            this.write(null, null)
        },
        write(start, end) {
            const value = cloneDeep(this.value)
            this.set(
                value,
                this.field.start,
                start && start.startOf('day').format()
            )
            this.set(value, this.field.end, end && end.endOf('day').format())
            this.$emit('input', value)
        },
        set(value, filter, date) {
            if (filter.type) {
                if (date) {
                    if (!value[filter.key]) value[filter.key] = {}
                    value[filter.key][filter.type] = date
                } else if (value[filter.key]) {
                    delete value[filter.key][filter.type]
                    if (!Object.keys(value[filter.key]).length)
                        delete value[filter.key]
                }
            } else if (date) value[filter.key] = date
            else delete value[filter.key]
        },
    },
}
</script>
